import React, { useEffect, useState } from 'react';
import {
  Paper,
  Typography,
  Box,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Chip,
  IconButton,
  Badge,
  Button,
  Collapse,
  Divider,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Campaign as CampaignIcon,
  Build as BuildIcon,
  Info as InfoIcon,
  NewReleases as NewReleasesIcon,
  CheckCircle as CheckCircleIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  DoneAll as DoneAllIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { mitteilungenApi } from '../services/api';
import type { Mitteilung, MitteilungenResponse } from '../types/api';

const getPriorityColor = (prioritaet: string): 'default' | 'primary' | 'warning' | 'error' => {
  switch (prioritaet) {
    case 'kritisch': return 'error';
    case 'hoch': return 'warning';
    case 'normal': return 'primary';
    default: return 'default';
  }
};

const getTypeIcon = (typ: string) => {
  switch (typ) {
    case 'ankuendigung': return <CampaignIcon />;
    case 'wartung': return <BuildIcon />;
    case 'release_note': return <NewReleasesIcon />;
    default: return <InfoIcon />;
  }
};

const getTypLabel = (typ: string): string => {
  switch (typ) {
    case 'ankuendigung': return 'Ankündigung';
    case 'wartung': return 'Wartung';
    case 'release_note': return 'Release';
    case 'info': return 'Info';
    default: return typ;
  }
};

const MitteilungenWidget: React.FC = () => {
  const navigate = useNavigate();
  const [mitteilungen, setMitteilungen] = useState<Mitteilung[]>([]);
  const [ungeleseneCount, setUngeleseneCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(true);

  useEffect(() => {
    loadMitteilungen();
  }, []);

  const loadMitteilungen = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await mitteilungenApi.getAll({ page: 1, pageSize: 5 });
      const data = response.data as MitteilungenResponse;
      setMitteilungen(data.mitteilungen || []);
      setUngeleseneCount(data.ungeleseneCount || 0);
    } catch (err: any) {
      console.error('Fehler beim Laden der Mitteilungen:', err);
      setError('Mitteilungen konnten nicht geladen werden');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (id: number, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await mitteilungenApi.alsGelesenMarkieren(id);
      setMitteilungen(prev =>
        prev.map(m => m.mitteilungID === id ? { ...m, istGelesen: true } : m)
      );
      setUngeleseneCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Fehler beim Markieren als gelesen:', err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await mitteilungenApi.alleAlsGelesenMarkieren();
      setMitteilungen(prev => prev.map(m => ({ ...m, istGelesen: true })));
      setUngeleseneCount(0);
    } catch (err) {
      console.error('Fehler beim Markieren aller als gelesen:', err);
    }
  };

  const handleNavigateToMitteilungen = () => {
    navigate('/mitteilungen');
  };

  if (loading) {
    return (
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <NotificationsIcon color="primary" />
          <Typography variant="h6">Mitteilungen</Typography>
        </Box>
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={24} />
        </Box>
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, mb: 4 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <NotificationsIcon color="primary" />
          <Typography variant="h6">Mitteilungen</Typography>
        </Box>
        <Typography color="error" sx={{ mt: 2 }}>{error}</Typography>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3, mb: 4 }}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Box display="flex" alignItems="center" gap={2}>
          <Badge badgeContent={ungeleseneCount} color="error">
            <NotificationsIcon color="primary" />
          </Badge>
          <Typography variant="h6">
            Mitteilungen
          </Typography>
        </Box>
        <Box display="flex" alignItems="center" gap={1}>
          {ungeleseneCount > 0 && (
            <IconButton
              size="small"
              onClick={handleMarkAllAsRead}
              title="Alle als gelesen markieren"
            >
              <DoneAllIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      <Collapse in={expanded}>
        {mitteilungen.length === 0 ? (
          <Typography color="textSecondary" textAlign="center" py={2}>
            Keine Mitteilungen vorhanden
          </Typography>
        ) : (
          <>
            <List disablePadding>
              {mitteilungen.map((mitteilung, index) => (
                <React.Fragment key={mitteilung.mitteilungID}>
                  <ListItem
                    sx={{
                      px: 1,
                      py: 1.5,
                      bgcolor: mitteilung.istGelesen ? 'transparent' : 'action.hover',
                      borderRadius: 1,
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'action.selected',
                      },
                    }}
                    onClick={() => navigate(`/mitteilungen/${mitteilung.mitteilungID}`)}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      {getTypeIcon(mitteilung.typ)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography
                            variant="body2"
                            fontWeight={mitteilung.istGelesen ? 'normal' : 'bold'}
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              maxWidth: 200,
                            }}
                          >
                            {mitteilung.titel}
                          </Typography>
                          {mitteilung.istAngepinnt && (
                            <Chip label="Wichtig" size="small" color="warning" sx={{ height: 20 }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box display="flex" alignItems="center" gap={1} mt={0.5}>
                          <Chip
                            label={getTypLabel(mitteilung.typ)}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          <Chip
                            label={mitteilung.prioritaet}
                            size="small"
                            color={getPriorityColor(mitteilung.prioritaet)}
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                          <Typography variant="caption" color="textSecondary">
                            {new Date(mitteilung.erstelltAm).toLocaleDateString('de-DE')}
                          </Typography>
                        </Box>
                      }
                    />
                    {!mitteilung.istGelesen && (
                      <IconButton
                        size="small"
                        onClick={(e) => handleMarkAsRead(mitteilung.mitteilungID, e)}
                        title="Als gelesen markieren"
                      >
                        <CheckCircleIcon fontSize="small" color="action" />
                      </IconButton>
                    )}
                  </ListItem>
                  {index < mitteilungen.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>

            <Box display="flex" justifyContent="center" mt={2}>
              <Button
                variant="text"
                color="primary"
                onClick={handleNavigateToMitteilungen}
              >
                Alle Mitteilungen anzeigen
              </Button>
            </Box>
          </>
        )}
      </Collapse>
    </Paper>
  );
};

export default MitteilungenWidget;
